import { ref, onMounted, onUnmounted } from 'vue'

// Site key do reCAPTCHA (v2 checkbox)
const SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY || ''

let scriptLoaded = false
let scriptLoading = false
let scriptCallbacks: Array<() => void> = []

function loadRecaptchaScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (scriptLoaded) {
      resolve()
      return
    }
    if (scriptLoading) {
      scriptCallbacks.push(() => resolve())
      return
    }
    scriptLoading = true
    
    const script = document.createElement('script')
    script.src = `https://www.google.com/recaptcha/api.js?render=explicit`
    script.async = true
    script.defer = true
    script.onload = () => {
      scriptLoaded = true
      scriptLoading = false
      resolve()
      scriptCallbacks.forEach(cb => cb())
      scriptCallbacks = []
    }
    script.onerror = () => {
      scriptLoading = false
      reject(new Error('Falha ao carregar reCAPTCHA'))
    }
    document.head.appendChild(script)
  })
}

export function useRecaptcha(containerId: string) {
  const token = ref<string>('')
  const isReady = ref(false)
  const isEnabled = ref(!!SITE_KEY)
  let widgetId: number | null = null

  const renderWidget = () => {
    if (!isEnabled.value || typeof window === 'undefined') return
    const grecaptcha = (window as any).grecaptcha
    if (!grecaptcha || typeof grecaptcha.render !== 'function') return
    
    const container = document.getElementById(containerId)
    if (!container) return
    
    widgetId = grecaptcha.render(container, {
      sitekey: SITE_KEY,
      callback: (response: string) => {
        token.value = response
      },
      'expired-callback': () => {
        token.value = ''
      },
      'error-callback': () => {
        token.value = ''
      }
    })
    isReady.value = true
  }

  const reset = () => {
    if (widgetId !== null && typeof window !== 'undefined') {
      const grecaptcha = (window as any).grecaptcha
      if (grecaptcha && typeof grecaptcha.reset === 'function') {
        grecaptcha.reset(widgetId)
        token.value = ''
      }
    }
  }

  onMounted(async () => {
    if (!isEnabled.value) return
    try {
      await loadRecaptchaScript()
      // grecaptcha pode não estar pronto imediatamente após o script carregar
      const grecaptcha = (window as any).grecaptcha
      if (grecaptcha && typeof grecaptcha.render === 'function') {
        renderWidget()
      } else {
        // Aguarda o grecaptcha ficar pronto
        const checkReady = setInterval(() => {
          const grecaptchaCheck = (window as any).grecaptcha
          if (grecaptchaCheck && typeof grecaptchaCheck.render === 'function') {
            clearInterval(checkReady)
            renderWidget()
          }
        }, 200)
        // Timeout de segurança
        setTimeout(() => clearInterval(checkReady), 10000)
      }
    } catch (e) {
      console.error('Erro ao carregar reCAPTCHA:', e)
    }
  })

  onUnmounted(() => {
    // Não removemos o script global para não afetar outras instâncias
  })

  return {
    token,
    isReady,
    isEnabled,
    reset
  }
}

declare global {
  interface Window {
    grecaptcha?: {
      render: (container: HTMLElement | string, options: any) => number
      reset: (widgetId?: number) => void
      getResponse: (widgetId?: number) => string
    }
  }
}
